// src/pages/DashboardHome.js
import React, { useEffect, useMemo, useState } from "react";
import {
  getOrgSelectorPresentation,
  useAuth,
} from "../context/AuthContext";
import { getOrganisationDisplayName } from "../utils/orgPresentation";
import { usePoints } from "../context/PointsContext";
import { Link, useNavigate } from "react-router-dom";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { collection, doc, getDocs, limit, query, setDoc, where } from "firebase/firestore";
import Card from "../ui/Card";
import Stat from "../ui/Stat";
import Progress from "../ui/Progress";
import Table from "../ui/Table";
import { computeOrgMetricsFromFirestore, createDemoOrg, exportOrgSessionsCsv, fetchOrgSessions, fetchOrgSettings, seedOrgSessionsToFirestore } from "../utils/orgData";
import { exportAuditCsv, fetchOrgActivity, fetchOrgAudit } from "../utils/orgAudit";
import { seedOrgUsers } from "../utils/orgUsers";
import { db } from "../firebase";
import { checkinCollectionPath, fetchRecentCheckins, resolveCheckinOrgId } from "../utils/checkins";
import { findNextMemberFollowUpSession, formatFollowUpDate, getFollowUpDisplayStatus } from "../utils/followUp";
import { getSessionDisplayName, getSessionDisplayText, getViewerSessionDisplayName, isOwnSession } from "../utils/sessionPrivacy";
import { formatSummarySnippetForAudience, formatSupportTextForAudience } from "../utils/summary";
import { logSessionListDiagnostics, sortSessionsByRecency } from "../utils/sessionOrdering";
import { findResumableOwnSession } from "../utils/sessionContinuity";
import { resolveCurrentRecordSummary } from "../utils/sessionScope";
import { filterCachedSessionsForViewer } from "../utils/sessionSync";

const GLOBAL_SESS_KEY = "nia_sessions";
const orgKey = (orgId) => `${GLOBAL_SESS_KEY}__org_${orgId || "org_default"}`;
const userKey = (uid, orgId) => (uid ? `${orgKey(orgId)}__uid_${uid}` : "");
const toMillis = (value) => {
  if (!value) return Date.now();
  if (typeof value === "number") return value;
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Date.now() : parsed;
};
const norm = (r) => ({
  ...r,
  id: r.id || r.createdAt || r.date || Date.now(),
  createdAt: toMillis(r.createdAt || r.date || Date.now()),
});
const formatDateSafe = (value) => {
  const ms = toMillis(value);
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString();
};
const newest = (arr) => sortSessionsByRecency(arr);
const readMemberFollowUpBannerState = (storageKey) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const pulseFallback = [
  { day: "Mon", sessions: 6 },
  { day: "Tue", sessions: 9 },
  { day: "Wed", sessions: 7 },
  { day: "Thu", sessions: 11 },
  { day: "Fri", sessions: 8 },
  { day: "Sat", sessions: 5 },
  { day: "Sun", sessions: 4 },
];

function HistoryMini({ bucketKey, label = "Recent Sessions", canOpen = true, viewerUserId = "", viewerRealName = "", viewerRole = "member", ownBucket = false }) {
  const navigate = useNavigate();
  const maskSessionText = (session, text) => getSessionDisplayText(text, session, viewerRole, {
    viewerUserId,
    viewerRealName,
  });
  const sessionAudience = (session) => (isOwnSession(session, viewerUserId) ? "self" : "reviewer");

  const sessions = useMemo(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(bucketKey) || "[]");
      const seen = new Set();
      return newest(raw.map((row) => ({
        ...norm(row),
        userId: row?.userId || (ownBucket ? viewerUserId || "" : ""),
        userEmail: row?.userEmail || "",
      }))).filter((s) => {
        const key = String(s.id);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }).slice(0, 6);
    } catch {
      return [];
    }
  }, [bucketKey, ownBucket, viewerUserId]);

  const openById = (id) => {
    if (!canOpen) return;
    navigate(`/chat?id=${id}`);
  };
  const openLatest = () => {
    if (!canOpen) return;
    if (sessions[0]?.id) openById(sessions[0].id);
    else navigate("/chat");
  };

  return (
    <Card title={label} className="dashboard-card-compact">
      <div className="dashboard-section-head">
        <div className="nia-subtitle">Latest conversations</div>
        {canOpen ? (
          <button className="btn btn-ghost" style={{ padding: "4px 8px" }} onClick={openLatest}>
            Open Chat
          </button>
        ) : (
          <span className="nia-pill">View only</span>
        )}
      </div>
      {sessions.length === 0 ? (
        <div className="nia-muted">No sessions yet. Start a chat with Nia.</div>
      ) : (
        <div className="nia-list dashboard-list-compact">
          {sessions.map((s) => {
            const currentSummary = resolveCurrentRecordSummary(s);
            const titleText = s.title ||
              (!s.sessionSummary ? s.issueSummary || "" : "") ||
              "Untitled conversation";
            const summaryPreview = currentSummary?.summaryText ||
              (!s.sessionSummary ? s.preview || "" : "");
            return (
              <button
                key={s.id}
                className="nia-list-btn"
                onClick={() => openById(s.id)}
                disabled={!canOpen}
                style={canOpen ? undefined : { cursor: "default" }}
              >
                <span className="title dashboard-history-item__title">
                  {maskSessionText(s, titleText)}
                </span>
                <span className="nia-pill">{formatDateSafe(s.createdAt)}</span>
                <span className="nia-muted" style={{ width: "100%", textAlign: "left" }}>
                  {getViewerSessionDisplayName(s, viewerRole, { viewerUserId, viewerRealName }, "Member")}
                </span>
                {summaryPreview ? (
                  <span className="nia-muted dashboard-history-item__summary" style={{ width: "100%", textAlign: "left" }}>
                    {maskSessionText(s, formatSummarySnippetForAudience(summaryPreview, sessionAudience(s), 120))}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function privacySafeOrgActivityLabel(type = "") {
  const value = String(type || "").trim().toLowerCase();
  if (!value) return "Organisation activity updated";
  if (value.includes("session") || value.includes("flag_case") || value.includes("follow")) {
    return "Member activity updated";
  }
  if (value.includes("user") || value.includes("role") || value.includes("email") || value.includes("password")) {
    return "Access activity updated";
  }
  return "Organisation activity updated";
}

function PulseChart({ data }) {
  return (
    <div style={{ height: 220, width: "100%" }}>
      <ResponsiveContainer width="100%" height="100%" minWidth={10} minHeight={10} debounce={120}>
        <AreaChart data={data}>
          <XAxis dataKey="day" stroke="var(--color-border-strong)" tick={{ fill: "var(--ui-muted)", fontSize: 12 }} />
          <YAxis stroke="var(--color-border-strong)" tick={{ fill: "var(--ui-muted)", fontSize: 12 }} />
          <Tooltip
            contentStyle={{
              background: "var(--ui-surface)",
              border: "1px solid var(--ui-border)",
              color: "var(--ui-text)",
            }}
          />
          <Area
            type="monotone"
            dataKey="sessions"
            stroke="var(--ui-accent)"
            strokeWidth={2}
            fill="var(--ui-accent)"
            fillOpacity={0.3}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function DashboardOrganizationSelector({
  activeOrgId,
  orgs = [],
  orgLoadStatus,
  orgLoadError = "",
  orgSelectionError = "",
  setActiveOrgId,
}) {
  const presentation = getOrgSelectorPresentation(orgLoadStatus, orgs);
  const statusMessage = orgLoadError || orgSelectionError || (
    !orgs.length ? presentation.statusMessage : ""
  );

  return (
    <>
      <select
        aria-label="Dashboard active organisation"
        value={activeOrgId || ""}
        onChange={(event) => setActiveOrgId(event.target.value)}
        disabled={presentation.disabled}
      >
        {presentation.optionLabel && !orgs.length && (
          <option value="">{presentation.optionLabel}</option>
        )}
        {orgs.map((org) => (
          <option key={org.id} value={org.id}>
            {getOrganisationDisplayName(org)}
          </option>
        ))}
      </select>
      {statusMessage && (
        <div role="status" className="nia-muted">{statusMessage}</div>
      )}
    </>
  );
}

export default function DashboardHome() {
  const {
    user,
    orgs,
    orgLoadStatus,
    orgLoadError,
    orgSelectionError,
    activeOrgId,
    setActiveOrgId,
    effectiveRole,
    updateDashboardActionState,
  } = useAuth();
  const navigate = useNavigate();
  const displayName = (user?.firstName || "").trim() || user?.username || "Member";
  const role = effectiveRole || user?.role || "member";
  const isInvestorActual = user?.role === "investor";
  const isSuperActual = user?.role === "superadmin";
  const [auditRows, setAuditRows] = useState([]);
  const [activityRows, setActivityRows] = useState([]);
  const [orgSettings, setOrgSettings] = useState({});
  const [newsItems, setNewsItems] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [newsError, setNewsError] = useState("");
  const [showPostLoginHint, setShowPostLoginHint] = useState(false);
  const [memberPromptSessions, setMemberPromptSessions] = useState([]);
  const [memberCheckinCount, setMemberCheckinCount] = useState(0);

  const isCoachish = role === "coach" || role === "superadmin";
  const isOrg = role === "admin" || role === "superadmin";
  const isSuper = role === "superadmin";
  const isMember = role === "member";
  const isAdmin = role === "admin";
  const hideSensitiveDashboardContent = isAdmin;
  const canOpenSessionChat = role === "member" || role === "coach" || role === "admin" || role === "superadmin" || role === "investor";
  const [coachFilter, setCoachFilter] = useState("all");
  const [ownResumeSessions, setOwnResumeSessions] = useState([]);
  const actionKey = `nia_actions_${user?.uid || user?.username || "anon"}`;
  const [actionState, setActionState] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(actionKey) || "{}");
    } catch {
      return {};
    }
  });

  const [liveMetrics, setLiveMetrics] = useState(null);
  let points = 0, messageCount = 0;
  try {
    const p = usePoints?.();
    if (p) {
      points = p.points || Number(localStorage.getItem("nia_points") || 0);
      messageCount = p.messageCount || Number(localStorage.getItem("nia_messages") || 0);
    } else {
      points = Number(localStorage.getItem("nia_points") || 0);
      messageCount = Number(localStorage.getItem("nia_messages") || 0);
    }
  } catch {}

  useEffect(() => {
    try {
      localStorage.setItem(actionKey, JSON.stringify(actionState));
    } catch {}
  }, [actionKey, actionState]);

  useEffect(() => {
    try {
      setActionState(JSON.parse(localStorage.getItem(actionKey) || "{}"));
    } catch {
      setActionState({});
    }
  }, [actionKey]);

  useEffect(() => {
    const persisted =
      user?.dashboardActionState && typeof user.dashboardActionState === "object"
        ? user.dashboardActionState
        : null;
    if (persisted) {
      setActionState((prev) => ({ ...prev, ...persisted }));
    }
  }, [user?.dashboardActionState]);

  const markActionDone = async (key) => {
    setActionState((prev) => ({ ...prev, [key]: true }));
    try {
      await updateDashboardActionState?.({ [key]: true });
    } catch {}
  };

  const orgId = resolveCheckinOrgId(user, activeOrgId, role);
  const checkinPath = checkinCollectionPath(orgId);
  const memberFollowUpBannerStateKey = `nia_member_followup_banner_${user?.uid || user?.username || "anon"}_${orgId}`;
  const orgType = (orgSettings?.type || "").toLowerCase();
  const isLiveOrg = orgId === "org_shuttle" || orgType === "pilot" || orgType === "live";
  const allowFallback = !isLiveOrg;
  const [memberFollowUpBannerState, setMemberFollowUpBannerState] = useState(() => readMemberFollowUpBannerState(memberFollowUpBannerStateKey));

  useEffect(() => {
    if (!user?.uid) return;
    fetchOrgSettings(user, orgId)
      .then((settings) => setOrgSettings(settings || {}))
      .catch(() => {});
  }, [user, orgId]);

  useEffect(() => {
    setMemberFollowUpBannerState(readMemberFollowUpBannerState(memberFollowUpBannerStateKey));
  }, [memberFollowUpBannerStateKey]);

  useEffect(() => {
    let mounted = true;
    if (!user?.uid || role !== "member") return undefined;
    fetchOrgSessions(user, orgId)
      .then((rows) => {
        if (!mounted) return;
        const sessions = Array.isArray(rows) ? sortSessionsByRecency(rows) : [];
        logSessionListDiagnostics("dashboard_member_prompt_sessions", sessions, "firestore:org_sessions");
        setMemberPromptSessions(sessions);
      })
      .catch(() => {
        if (mounted) setMemberPromptSessions([]);
      });
    return () => {
      mounted = false;
    };
  }, [user, orgId, role]);

  useEffect(() => {
    let mounted = true;
    if (!user?.uid) return undefined;
    fetchOrgSessions(user, orgId)
      .then((rows) => {
        if (!mounted) return;
        const ownRows = (Array.isArray(rows) ? sortSessionsByRecency(rows) : [])
          .filter((session) => String(session?.userId || "") === String(user.uid));
        setOwnResumeSessions(ownRows);
      })
      .catch(() => {
        if (mounted) setOwnResumeSessions([]);
      });
    return () => {
      mounted = false;
    };
  }, [user, user?.uid, orgId]);

  useEffect(() => {
    let mounted = true;
    if (!user?.uid || role !== "member") return undefined;
    fetchRecentCheckins(orgId, { max: 20, userId: user.uid })
      .then((rows) => {
        if (!mounted) return;
        const count = Array.isArray(rows) ? rows.length : 0;
        setMemberCheckinCount(count);
        try {
          // eslint-disable-next-line no-console
          console.info("[checkins] checkInDashboardCount", {
            count,
            path: checkinPath,
            orgId,
            userIdPresent: Boolean(user?.uid),
          });
        } catch {}
      })
      .catch((error) => {
        if (!mounted) return;
        setMemberCheckinCount(0);
        try {
          // eslint-disable-next-line no-console
          console.warn("[checkins] checkInDashboardCount", {
            count: 0,
            path: checkinPath,
            orgId,
            userIdPresent: Boolean(user?.uid),
            code: error?.code || "",
            message: error?.message || "",
          });
        } catch {}
      });
    return () => {
      mounted = false;
    };
  }, [user?.uid, role, orgId, checkinPath]);

  const sessionCount = useMemo(() => {
    if (isLiveOrg) return 0;
    try {
      const key = userKey(user?.uid || "", orgId);
      if (!key) return 0;
      const raw = JSON.parse(localStorage.getItem(key) || "[]");
      return filterCachedSessionsForViewer(raw, user).length;
    } catch {
      return 0;
    }
  }, [user, orgId, isLiveOrg]);

  const bucketKey = !isOrg && !isCoachish ? userKey(user?.uid, orgId) : orgKey(orgId);

  const sessions = useMemo(() => {
    if (isLiveOrg) return [];
    try {
      const raw = JSON.parse(localStorage.getItem(bucketKey) || "[]");
      return newest(filterCachedSessionsForViewer(raw, user).map(norm));
    } catch {
      return [];
    }
  }, [bucketKey, isLiveOrg, user]);

  const liveSessions = liveMetrics?.recentSessions || sessions;

  const coachFiltered = useMemo(() => {
    if (!isCoachish || isOrg) return liveSessions;
    if (coachFilter === "last7") {
      const cutoff = Date.now() - 7 * 24 * 3600 * 1000;
      return liveSessions.filter((s) => new Date(s.createdAt || s.date || Date.now()).getTime() >= cutoff);
    }
    if (coachFilter === "active") {
      const counts = liveSessions.reduce((acc, s) => {
        const key = s.user || s.client || "Unknown";
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      return liveSessions.filter((s) => counts[s.user || s.client || "Unknown"] >= 2);
    }
    return liveSessions;
  }, [liveSessions, coachFilter, isCoachish, isOrg]);

  const recentRows = coachFiltered.slice(0, 5).map((s) => {
    const currentSummary = resolveCurrentRecordSummary(s);
    const summaryText = currentSummary?.summaryText ||
      (s.sessionSummary ? "No current summary" : s.issueSummary || "Session update");
    return [
      getSessionDisplayName(s, role, "Member"),
      formatDateSafe(s.createdAt),
      getSessionDisplayText(formatSummarySnippetForAudience(summaryText, isOwnSession(s, user?.uid || "") ? "self" : "reviewer", 60), s, role, {
        viewerUserId: user?.uid || "",
        viewerRealName: displayName,
      }),
    ];
  });
  const adminRecentRows = useMemo(
    () => coachFiltered.slice(0, 5).map((s) => [
      "Member activity updated",
      formatDateSafe(s.createdAt),
    ]),
    [coachFiltered]
  );
  const adminOrgActivityRows = useMemo(
    () => activityRows.map((row) => [
      privacySafeOrgActivityLabel(row.type),
      formatDateSafe(row.createdAt),
    ]),
    [activityRows]
  );
  const topActiveClients = useMemo(() => {
    const counts = coachFiltered.reduce((acc, s) => {
      const key = s.user || s.client || "Unknown";
      const prev = acc[key] || { count: 0, session: s };
      prev.count += 1;
      prev.session = s;
      acc[key] = prev;
      return acc;
    }, {});
    return Object.values(counts)
      .sort((a, b) => b.count - a.count)
      .slice(0, 4);
  }, [coachFiltered]);

  const pulse = useMemo(() => {
    if (!liveSessions.length) return allowFallback ? pulseFallback : [];
    const buckets = new Map();
    for (let i = 6; i >= 0; i -= 1) {
      const d = new Date(Date.now() - i * 24 * 3600 * 1000);
      buckets.set(d.toLocaleDateString(), { day: d.toLocaleDateString(undefined, { weekday: "short" }), sessions: 0 });
    }
    liveSessions.forEach((s) => {
      const dayKey = new Date(toMillis(s.createdAt)).toLocaleDateString();
      if (buckets.has(dayKey)) {
        buckets.get(dayKey).sessions += 1;
      }
    });
    return Array.from(buckets.values());
  }, [liveSessions, allowFallback]);
  const memberFollowUpPrompt = useMemo(
    () => (
      role === "member"
        ? findNextMemberFollowUpSession(
            isLiveOrg ? memberPromptSessions : sessions,
            Date.now(),
            memberFollowUpBannerState
          )
        : null
    ),
    [role, isLiveOrg, memberPromptSessions, sessions, memberFollowUpBannerState]
  );
  const memberFollowUpDisplayStatus = useMemo(
    () => getFollowUpDisplayStatus(memberFollowUpPrompt?.followUp),
    [memberFollowUpPrompt]
  );
  const ownResumeSession = useMemo(
    () => findResumableOwnSession(isLiveOrg ? ownResumeSessions : sessions, user?.uid || ""),
    [isLiveOrg, ownResumeSessions, sessions, user?.uid]
  );
  const primaryChatTarget = ownResumeSession?.id
    ? `/chat?id=${ownResumeSession.id}`
    : (canOpenSessionChat ? "/chat" : "/org/insights");
  const primaryChatLabel = ownResumeSession?.id
    ? "Resume session"
    : (canOpenSessionChat ? "Start your next session" : "Open org insights");
  const handleContinueWithFollowUp = (prompt) => {
    const sessionId = String(prompt?.sessionId || "");
    const scheduledFor = String(prompt?.followUp?.scheduledFor || "");
    const resumedAt = new Date().toISOString();
    if (!sessionId || !scheduledFor) {
      navigate("/chat");
      return;
    }
    const bannerKey = `${sessionId}::${scheduledFor}`;
    const nextState = {
      ...memberFollowUpBannerState,
      [bannerKey]: {
        lastOpenedAt: resumedAt,
        resumedAt,
      },
    };
    setMemberFollowUpBannerState(nextState);
    try {
      localStorage.setItem(memberFollowUpBannerStateKey, JSON.stringify(nextState));
    } catch {}
    setMemberPromptSessions((prev) =>
      prev.map((session) =>
        String(session?.id || session?.createdAt || "") === sessionId
          ? {
              ...session,
              followUp: {
                ...(session?.followUp || {}),
                ...(prompt?.followUp || {}),
                lastOpenedAt: resumedAt,
                resumedAt,
                updatedAt: resumedAt,
                updatedByType: "nia",
              },
            }
          : session
      )
    );
    if (user?.uid) {
      setDoc(
        doc(db, "orgs", orgId, "sessions", sessionId),
        {
          followUp: {
            ...(prompt?.followUp || {}),
            lastOpenedAt: resumedAt,
            resumedAt,
            updatedAt: resumedAt,
            updatedByType: "nia",
          },
        },
        { merge: true }
      ).catch(() => {});
    }
    navigate(`/chat?id=${sessionId}&resume=followup`);
  };

  const monthlySessionCount = Number.isFinite(Number(liveMetrics?.kpis?.sessionsMonth))
    ? Number(liveMetrics.kpis.sessionsMonth)
    : sessions.length;
  const goalPct = monthlySessionCount > 0 || allowFallback
    ? Math.min(100, Math.round((monthlySessionCount / 30) * 100))
    : 0;
  const hasProfileBasics = Boolean((user?.firstName || "").trim() || user?.photoDataUrl || user?.photoURL);
  const hasPrefsStep = Boolean(actionState.prefs);
  const hasChatStep = Boolean(liveSessions.length > 0 || sessionCount > 0);
  const hasCheckinStep = Boolean(memberCheckinCount > 0);
  const hasGamesStep = points > 0;
  const gettingStartedSteps = [
    { id: "profile", label: "Complete your profile", done: hasProfileBasics, cta: "Profile", to: "/profile" },
    { id: "prefs", label: "Set your preferences", done: hasPrefsStep, cta: "Preferences", to: "/preferences" },
    { id: "chat", label: "Start your first chat", done: hasChatStep, cta: "Open Chat", to: "/chat" },
    { id: "checkin", label: "Submit your first check-in", done: hasCheckinStep, cta: "Check-in", to: "/checkin" },
    { id: "games", label: "Try one game", done: hasGamesStep, cta: "Open Games", to: "/games" },
  ];
  const gettingStartedDone = gettingStartedSteps.filter((step) => step.done).length;
  const shouldShowGettingStarted = !isMember && role !== "admin" && gettingStartedDone < 3;
  const memberNextBestActions = gettingStartedSteps.filter((step) => !step.done);
  const nonMemberNextBestActions = [
    {
      id: "prefs",
      label: "Complete preferences",
      done: Boolean(actionState.prefs),
      cta: "Open",
      to: "/preferences",
      onClick: () => markActionDone("prefs"),
    },
    {
      id: "chat",
      label: canOpenSessionChat ? "Start your first session" : "Review org insights",
      done: Boolean(actionState.chat || hasChatStep),
      cta: canOpenSessionChat ? "Open Chat" : "Open",
      to: canOpenSessionChat ? "/chat" : "/org/insights",
      onClick: () => markActionDone("chat"),
    },
    ...(isOrg
      ? [{
          id: "org_admin",
          label: "Review org settings",
          done: Boolean(actionState.org_admin),
          cta: "Org Admin",
          to: "/org/admin",
          onClick: () => markActionDone("org_admin"),
        }]
      : []),
    ...(isCoachish
      ? [{
          id: "coach_dash",
          label: "Open coach dashboard",
          done: Boolean(actionState.coach_dash),
          cta: "Coach",
          to: "/coach",
          onClick: () => markActionDone("coach_dash"),
        }]
      : []),
    ...(isSuper
      ? [{
          id: "export_sessions",
          label: "Export org sessions",
          done: Boolean(actionState.export_sessions),
          cta: "Export",
          onClick: () => {
            exportOrgSessionsCsv(user, activeOrgId || user?.orgId || "org_default");
            markActionDone("export_sessions");
          },
        },
        {
          id: "export_audit",
          label: "Export audit log",
          done: Boolean(actionState.export_audit),
          cta: "Export",
          onClick: () => {
            exportAuditCsv(auditRows, activeOrgId || user?.orgId || "org_default");
            markActionDone("export_audit");
          },
        }]
      : []),
    ...((isSuperActual || isInvestorActual)
      ? [{
          id: "investor_showcase",
          label: "View investor showcase",
          done: Boolean(actionState.investor_showcase),
          cta: "Open",
          to: "/investor",
          onClick: () => markActionDone("investor_showcase"),
        },
        {
          id: "demo_walk",
          label: "Run demo walkthrough",
          done: Boolean(actionState.demo_walk),
          cta: "Start",
          to: "/demo",
          onClick: () => markActionDone("demo_walk"),
        }]
      : []),
  ];
  const visibleNonMemberNextBestActions = nonMemberNextBestActions.filter((action) => !action.done);
  const quickActionTotal = nonMemberNextBestActions.length;
  const quickActionDone = nonMemberNextBestActions.filter((action) => action.done).length;

  useEffect(() => {
    let mounted = true;
    if (!user?.uid) return undefined;
    const orgId = activeOrgId || user.orgId || "org_default";
    computeOrgMetricsFromFirestore(user, orgId, { planSeats: 30, allowFallback })
      .then((metrics) => {
        if (mounted) {
          logSessionListDiagnostics("dashboard_recent_sessions", metrics?.recentSessions || [], "firestore:computed_org_metrics");
          setLiveMetrics(metrics);
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [user, activeOrgId, allowFallback]);

  useEffect(() => {
    if (!user?.uid || user?.role !== "superadmin") return;
    fetchOrgAudit(user, activeOrgId || user?.orgId || "org_default", 50)
      .then((rows) => setAuditRows(rows))
      .catch(() => {});
  }, [user, activeOrgId]);

  useEffect(() => {
    if (!user?.uid) return;
    fetchOrgActivity(user, activeOrgId || user?.orgId || "org_default", 6)
      .then((rows) => setActivityRows(rows))
      .catch(() => {});
  }, [user, activeOrgId]);

  useEffect(() => {
    try {
      const shouldShow = sessionStorage.getItem("nia_post_login_hint") === "1";
      setShowPostLoginHint(shouldShow);
      if (shouldShow) sessionStorage.removeItem("nia_post_login_hint");
    } catch {
      setShowPostLoginHint(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.uid) return;
    let mounted = true;
    setNewsLoading(true);
    setNewsError("");
    const loadAnnouncements = async () => {
      try {
        const q = query(
          collection(db, "announcements"),
          where("active", "==", true),
          limit(20)
        );
        const snap = await getDocs(q);
        if (!mounted) return;
        const rows = snap.docs.map((docSnap) => {
          const data = docSnap.data() || {};
          return {
            id: docSnap.id,
            title: String(data.title || "Announcement"),
            detail: String(data.detail || ""),
            tag: String(data.tag || "Update"),
            createdAt: toMillis(data.createdAt || 0),
          };
        });
        rows.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setNewsItems(rows.slice(0, 3));
      } catch {
        if (!mounted) return;
        setNewsItems([]);
        setNewsError("Unable to load announcements right now.");
      } finally {
        if (mounted) setNewsLoading(false);
      }
    };
    loadAnnouncements();
    return () => {
      mounted = false;
    };
  }, [user?.uid]);


  return (
    <div className="nia-page dashboard-home">
      <Card className="nia-hero dashboard-hero">
        <div className="nia-header">
          <div className="dashboard-hero-copy">
            <div className="nia-subtitle">Today at a glance</div>
            <h2>Welcome back, {displayName}.</h2>
            <p>Track momentum, open your next action, and keep coaching outcomes moving.</p>
            <div className="dashboard-hero-meta">
              <span className="nia-pill">{role}</span>
              <span className="nia-pill">{isLiveOrg ? "Pilot/live mode" : "Demo mode"}</span>
              <span className="nia-pill">{liveSessions.length} sessions in view</span>
            </div>
          </div>
          <div className="dashboard-hero-actions">
            <div className="dashboard-hero-cta">
              <Link className="btn btn-primary" to={primaryChatTarget}>
                {primaryChatLabel}
              </Link>
              <Link className="btn btn-ghost" to={role === "admin" ? "/org/admin" : "/games"}>
                {role === "admin" ? "Open org admin" : "Open games"}
              </Link>
            </div>
            <div className="dashboard-momentum-strip">
              <div className="nia-subtitle">Momentum</div>
              <Progress value={goalPct} />
              <div className="nia-muted">{goalPct}% toward the monthly session target.</div>
            </div>
          </div>
        </div>
      </Card>

      {showPostLoginHint && (
        <Card className="dashboard-card-compact">
          <div className="dashboard-section-head">
            <div className="nia-subtitle">You are signed in</div>
            <button className="btn btn-ghost" onClick={() => setShowPostLoginHint(false)}>Dismiss</button>
          </div>
          <div className="nia-muted">
            Next step: open Profile to confirm your details and update your password if you used a temporary one.
          </div>
          <div style={{ marginTop: 10 }}>
            <Link className="btn btn-ghost" to="/profile">Open Profile</Link>
          </div>
        </Card>
      )}

      {isMember && (
        <Card className="checkin-cta">
          <div className="nia-header">
            <div>
              <h3>Daily Check‑in</h3>
              <p>Share how you feel today so your coach can support you better.</p>
            </div>
            <Link className="btn btn-primary" to="/checkin">Check‑in now</Link>
          </div>
        </Card>
      )}

      {isMember && memberFollowUpPrompt && (
        <Card className="dashboard-card-compact">
          <div className="dashboard-section-head">
            <div>
              <div className="nia-subtitle">
                {memberFollowUpDisplayStatus === "resumed"
                  ? "Follow-up in progress"
                  : Date.parse(memberFollowUpPrompt.followUp?.scheduledFor || "") > Date.now()
                  ? "Upcoming Follow-up Session"
                  : "Your Follow-up Session Is Ready"}
              </div>
              <div className="nia-muted" style={{ marginTop: 4 }}>
                Scheduled for {formatFollowUpDate(memberFollowUpPrompt.followUp.scheduledFor)}
              </div>
            </div>
            <span className="nia-pill">
              {memberFollowUpDisplayStatus === "resumed" ? "In progress" : memberFollowUpPrompt.followUp.status === "scheduled" ? "Active follow-up" : "Follow-up"}
            </span>
          </div>
          <div style={{ marginTop: 10 }}>
            {formatSupportTextForAudience(memberFollowUpPrompt.followUp.nextStepText, "user")}
          </div>
          <div style={{ marginTop: 12 }}>
            <button className="btn btn-primary" onClick={() => handleContinueWithFollowUp(memberFollowUpPrompt)}>
              Resume follow-up
            </button>
          </div>
        </Card>
      )}


      {isSuper && (
        <Card title="Impersonate Organisation">
          <div className="nia-form">
            <label>Active org</label>
            <DashboardOrganizationSelector
              activeOrgId={activeOrgId}
              orgs={orgs}
              orgLoadStatus={orgLoadStatus}
              orgLoadError={orgLoadError}
              orgSelectionError={orgSelectionError}
              setActiveOrgId={setActiveOrgId}
            />
            <div className="nia-muted">Switching org updates insights and sessions.</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
              <button
                className="btn btn-primary"
                onClick={async () => {
                  const id = await createDemoOrg(user);
                  if (id) {
                    await seedOrgSessionsToFirestore(user, id);
                    await seedOrgUsers(user, id);
                    setActiveOrgId(id);
                  }
                }}
              >
                Create Demo Org
              </button>
              <button
                className="btn btn-ghost"
                onClick={() => exportOrgSessionsCsv(user, activeOrgId || user?.orgId || "org_default")}
              >
                Export CSV
              </button>
            </div>
          </div>
        </Card>
      )}

      <div className="nia-grid nia-grid--4">
        {isMember && (
          <>
            <Stat label="Points" value={points} subtle="Engagement rewards" />
            <Stat
              label="Messages"
              value={messageCount}
              subtle={messageCount > 0 ? "Your coaching depth" : "Start chat to begin your journey"}
            />
            <Stat
              label="Sessions"
              value={liveSessions.length}
              subtle={liveSessions.length > 0 ? "Your history" : "No sessions yet"}
            />
            <Stat
              label="Check-ins"
              value={memberCheckinCount}
              subtle={memberCheckinCount > 0 ? "Consistency streaks" : "Complete your first check-in"}
            />
          </>
        )}
        {isCoachish && !isOrg && (
          <>
            <Stat label="Active clients" value={Math.max(1, liveMetrics?.kpis?.activeUsersWeek || 0)} subtle="This week" />
            <Stat label="Sessions" value={liveSessions.length} subtle="Across clients" />
            <Stat label="Avg msgs" value={liveMetrics?.kpis?.avgMsgsPerSession?.toFixed?.(1) || "0.0"} subtle="Depth" />
            <Stat label="Points" value={points} subtle="Coach momentum" />
          </>
        )}
        {isOrg && (
          <>
            <Stat label="Sessions" value={liveMetrics?.kpis?.sessionsWeek ?? 0} subtle="This week" />
            <Stat label="Active users" value={liveMetrics?.kpis?.activeUsersWeek ?? 0} subtle="7-day view" />
            <Stat label="Avg msgs" value={liveMetrics?.kpis?.avgMsgsPerSession?.toFixed?.(1) || "0.0"} subtle="Depth" />
            <Stat label="Seats used" value={`${liveMetrics?.seats?.used ?? 0}/${liveMetrics?.seats?.total ?? 0}`} subtle="Plan" />
          </>
        )}
      </div>

      {shouldShowGettingStarted && (
        <Card title="Getting Started" className="dashboard-card-compact">
          <div className="dashboard-section-head">
            <div className="nia-subtitle">First steps</div>
            <span className="nia-pill">{gettingStartedDone}/{gettingStartedSteps.length} complete</span>
          </div>
          <div className="nia-list dashboard-list-compact">
            {gettingStartedSteps.map((step) => (
              <div key={step.id} className="nia-list-btn" style={{ cursor: "default" }}>
                <span>{step.label}</span>
                <span className="nia-pill">{step.done ? "Done" : "Next"}</span>
                <Link
                  className="btn btn-ghost"
                  to={step.to}
                  onClick={() => {
                    if (step.id === "prefs") markActionDone("prefs");
                  }}
                >
                  {step.cta}
                </Link>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card title="Engagement Pulse">
        {pulse.length === 0 ? (
          <div className="nia-muted">
            No activity trend yet. Start with a quick session to unlock your pulse.
            <div style={{ marginTop: 10 }}>
              <Link className="btn btn-ghost" to={canOpenSessionChat ? "/chat" : "/org/insights"}>
                {canOpenSessionChat ? "Start Chat" : "Open Org Insights"}
              </Link>
            </div>
          </div>
        ) : (
          <PulseChart data={pulse} />
        )}
        <div style={{ marginTop: 12 }}>
          <Progress value={goalPct} />
          <div className="nia-muted" style={{ marginTop: 8 }}>
            {liveSessions.length > 0
              ? `${goalPct}% toward the 30-session monthly goal.`
              : "Your momentum will appear after your first sessions."}
          </div>
        </div>
      </Card>

      <div className="nia-grid nia-grid--2">
        {hideSensitiveDashboardContent ? (
          <Card title="Recent Sessions" className="dashboard-card-compact">
            <div className="nia-muted">
              Recent session titles and previews are hidden for admin users.
            </div>
            <div style={{ marginTop: 10 }}>
              <Link className="btn btn-ghost" to="/org/insights">
                Open Org Insights
              </Link>
            </div>
          </Card>
        ) : (
          <HistoryMini
            bucketKey={bucketKey}
            label={isOrg || isCoachish ? "Recent Sessions" : "Your Sessions"}
            canOpen={canOpenSessionChat}
            viewerUserId={user?.uid || ""}
            viewerRealName={displayName}
            viewerRole={role}
            ownBucket={!isOrg && !isCoachish}
          />
        )}
        <Card title={isMember ? "Next Best Actions" : "Quick Actions"} className="dashboard-card-compact">
          <div className="dashboard-section-head">
            <div className="nia-subtitle">{isMember ? "Recommended next steps" : "Next best actions"}</div>
            <span className="nia-pill">
              {isMember ? `${gettingStartedDone}/${gettingStartedSteps.length} complete` : `${quickActionDone}/${quickActionTotal} done`}
            </span>
          </div>
          <div className="nia-list dashboard-list-compact" style={{ marginTop: 10 }}>
            {isMember ? (
              memberNextBestActions.length > 0 ? (
                memberNextBestActions.map((step) => (
                  <div key={step.id} className="nia-list-btn" style={{ cursor: "default" }}>
                    <span>{step.label}</span>
                    <span className="nia-pill">Next</span>
                    <Link
                      className="btn btn-ghost"
                      to={step.to}
                      onClick={() => {
                        if (step.id === "prefs") markActionDone("prefs");
                      }}
                    >
                      {step.cta}
                    </Link>
                  </div>
                ))
              ) : (
                <div className="nia-muted">
                  You are up to date. Your next coaching steps will appear here as you use the app.
                </div>
              )
            ) : (
              visibleNonMemberNextBestActions.length > 0 ? (
                visibleNonMemberNextBestActions.map((action) => (
                  <div key={action.id} className="nia-list-btn" style={{ cursor: "default" }}>
                    <span>{action.label}</span>
                    <span className="nia-pill">Next</span>
                    {action.to ? (
                      <Link className="btn btn-ghost" to={action.to} onClick={action.onClick}>
                        {action.cta}
                      </Link>
                    ) : (
                      <button className="btn btn-ghost" onClick={action.onClick}>
                        {action.cta}
                      </button>
                    )}
                  </div>
                ))
              ) : (
                <div className="nia-muted">
                  You are up to date. Your next coaching steps will appear here as you use the app.
                </div>
              )
            )}
          </div>
        </Card>
      </div>

      {isCoachish && !isOrg && (
        <div className="nia-grid nia-grid--2">
          <Card title="Coach Filters">
            <div className="nia-subtitle">Quick view</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
              <button className="btn btn-ghost" onClick={() => setCoachFilter("all")}>All sessions</button>
              <button className="btn btn-ghost" onClick={() => setCoachFilter("last7")}>Last 7 days</button>
              <button className="btn btn-ghost" onClick={() => setCoachFilter("active")}>Active clients</button>
            </div>
          </Card>

          <Card title="Top Active Clients" className="dashboard-card-compact">
            <div className="nia-list dashboard-list-compact">
              {topActiveClients.length === 0 ? (
                <div className="nia-muted">
                  No active clients yet. Run a first session to build this list.
                  <div style={{ marginTop: 10 }}>
                    <Link className="btn btn-ghost" to="/chat">Open Chat</Link>
                  </div>
                </div>
              ) : (
                topActiveClients.map((entry) => (
                  <div key={entry.session?.id || entry.session?.createdAt || entry.count} className="nia-list-btn" style={{ cursor: "default" }}>
                    <span>{getSessionDisplayName(entry.session, role, "Member")}</span>
                    <span className="nia-pill">{entry.count} sessions</span>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
      )}

      <div className="nia-grid nia-grid--2">
        <Card title="Recent Activity" className="dashboard-card-compact dashboard-table-compact">
          {(hideSensitiveDashboardContent ? adminRecentRows : recentRows).length > 0 ? (
            <Table
              headers={hideSensitiveDashboardContent ? ["Activity", "Date"] : ["User", "Date", "Highlight"]}
              rows={hideSensitiveDashboardContent ? adminRecentRows : recentRows}
            />
          ) : (
            <div className="nia-muted">
              Activity appears here after your first coaching session.
              <div style={{ marginTop: 10 }}>
                <Link className="btn btn-ghost" to={canOpenSessionChat ? "/chat" : "/org/insights"}>
                  {canOpenSessionChat ? "Start Chat" : "Open Org Insights"}
                </Link>
              </div>
            </div>
          )}
        </Card>

        {isOrg || isCoachish || isSuper ? (
          <Card title="Org Activity" className="dashboard-card-compact">
            {(hideSensitiveDashboardContent ? adminOrgActivityRows : activityRows).length === 0 ? (
              <div className="nia-muted">
                No recent org activity yet.
                <div style={{ marginTop: 10 }}>
                  <Link className="btn btn-ghost" to={canOpenSessionChat ? "/chat" : "/org/insights"}>
                    {canOpenSessionChat ? "Start first session" : "Open Org Insights"}
                  </Link>
                </div>
              </div>
            ) : (
              <div className="nia-list dashboard-list-compact">
                {hideSensitiveDashboardContent
                  ? adminOrgActivityRows.map((row, idx) => (
                      <div key={`${row[0]}-${idx}`} className="nia-list-btn" style={{ cursor: "default" }}>
                        <span>{row[0]}</span>
                        <span className="nia-pill">{row[1]}</span>
                      </div>
                    ))
                  : activityRows.map((row) => (
                      <div key={row.id} className="nia-list-btn" style={{ cursor: "default" }}>
                        <span>{privacySafeOrgActivityLabel(row.type)}</span>
                        <span className="nia-pill">{row.type || "event"}</span>
                        <span className="nia-pill">{formatDateSafe(row.createdAt)}</span>
                      </div>
                    ))}
              </div>
            )}
          </Card>
        ) : !isMember ? (
          <Card title="Quick Wins">
            <div className="nia-list dashboard-list-compact">
              <div className="nia-list-btn">
                <span>Review active clients</span>
                <span className="nia-pill">Now</span>
              </div>
              <div className="nia-list-btn">
                <span>Schedule a follow‑up</span>
                <span className="nia-pill">Today</span>
              </div>
              <div className="nia-list-btn">
                <span>Send a check‑in prompt</span>
                <span className="nia-pill">New</span>
              </div>
            </div>
          </Card>
        ) : null}

        <Card title="News & Releases" className="dashboard-card-compact">
          {newsLoading ? (
            <div className="nia-muted">Loading announcements...</div>
          ) : newsError ? (
            <div className="nia-muted">{newsError}</div>
          ) : newsItems.length === 0 ? (
            <div className="nia-muted">
              No announcements yet.
              {(isOrg || isSuper) && (
                <div style={{ marginTop: 10 }}>
                  <Link className="btn btn-ghost" to="/org/admin">Open Org Admin</Link>
                </div>
              )}
            </div>
          ) : (
            <div className="nia-list dashboard-list-compact">
              {newsItems.map((item, idx) => (
                <div key={item.id || `${item.title}-${idx}`} className="nia-list-btn" style={{ cursor: "default" }}>
                  <div className="title">
                    <div style={{ fontWeight: 600 }}>{item.title}</div>
                    <div className="nia-muted" style={{ marginTop: 2 }}>{item.detail}</div>
                  </div>
                  <span className="nia-pill">{item.tag}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
